<template>
  <el-dialog
    title="AI 规则库 - 思维导图"
    :visible.sync="dialogVisible"
    fullscreen
    append-to-body
    :close-on-click-modal="false"
    custom-class="mindmap-dialog"
    @opened="onOpened"
    @close="onClose">
    <div class="mindmap-toolbar">
      <span class="title-hint">
        <i class="el-icon-share"></i>
        共 {{ totalRules }} 条规则 / {{ totalItems }} 个检查项 ·
        <span style="color:#999">点击节点展开/收起,点 ⓘ 查看详情</span>
      </span>
      <div class="toolbar-right">
        <el-radio-group v-model="groupBy" size="mini" @change="rebuild">
          <el-radio-button label="scene">按场景分组</el-radio-button>
          <el-radio-button label="category">按大类分组</el-radio-button>
          <el-radio-button label="feeType">按费用分组</el-radio-button>
        </el-radio-group>
        <el-button size="mini" icon="el-icon-zoom-in" @click="zoom(1.2)">放大</el-button>
        <el-button size="mini" icon="el-icon-zoom-out" @click="zoom(0.8)">缩小</el-button>
        <el-button size="mini" icon="el-icon-refresh" @click="resetView">还原</el-button>
        <el-button size="mini" icon="el-icon-arrow-down" @click="expandAll">全部展开</el-button>
        <el-button size="mini" icon="el-icon-arrow-up" @click="collapseAll">全部收起</el-button>
      </div>
    </div>

    <div class="mindmap-canvas-wrap" ref="canvasWrap"
         @mousedown="onPanStart"
         @mousemove="onPanMove"
         @mouseup="onPanEnd"
         @mouseleave="onPanEnd"
         @wheel.prevent="onWheel">
      <div v-loading="loading" element-loading-text="加载规则数据...">
        <svg
          ref="canvas"
          class="mindmap-svg"
          :width="canvasWidth"
          :height="canvasHeight"
          :style="{ transform: `translate(${panX}px, ${panY}px) scale(${scale})` }">
          <!-- 连接线 -->
          <g class="links">
            <path
              v-for="link in visibleLinks"
              :key="link.id"
              :d="link.path"
              :stroke="link.color"
              stroke-width="2"
              fill="none"
              opacity="0.6" />
          </g>
          <!-- 节点 -->
          <g class="nodes">
            <g
              v-for="node in visibleNodes"
              :key="node.id"
              :transform="`translate(${node.x}, ${node.y})`"
              :class="['node', `node-${node.type}`, { collapsed: !node.expanded && node.hasChildren }]"
              @click="onNodeClick(node)">
              <!-- 节点矩形 -->
              <rect
                :x="-node.width/2"
                :y="-node.height/2"
                :width="node.width"
                :height="node.height"
                :rx="node.type === 'root' ? 24 : 8"
                :fill="node.bgColor"
                :stroke="node.borderColor"
                stroke-width="1.5" />
              <!-- 文字 -->
              <text
                :x="0"
                :y="0"
                text-anchor="middle"
                dominant-baseline="central"
                :fill="node.textColor"
                :font-size="node.fontSize"
                :font-weight="node.type === 'root' || node.type === 'group' ? 'bold' : 'normal'">
                {{ node.label }}
              </text>
              <!-- 子节点数量徽章 -->
              <g v-if="node.hasChildren" :transform="`translate(${node.width/2 - 4}, ${-node.height/2 + 4})`">
                <circle r="9" fill="#fff" stroke="#bbb" stroke-width="1" />
                <text x="0" y="0" text-anchor="middle" dominant-baseline="central"
                      fill="#666" font-size="10">
                  {{ node.expanded ? '−' : node.childCount }}
                </text>
              </g>
              <!-- 详情按钮(仅 rule 和 item 节点) -->
              <g v-if="node.type === 'rule' || node.type === 'item'"
                 :transform="`translate(${node.width/2 + 14}, 0)`"
                 @click.stop="showDetail(node)">
                <circle r="9" fill="#fff" stroke="#409EFF" stroke-width="1.2" />
                <text x="0" y="0" text-anchor="middle" dominant-baseline="central"
                      fill="#409EFF" font-size="11" font-weight="bold">i</text>
              </g>
            </g>
          </g>
        </svg>
      </div>
    </div>

    <!-- 节点详情抽屉 -->
    <el-drawer
      title="节点详情"
      :visible.sync="detailVisible"
      direction="rtl"
      size="500px"
      append-to-body>
      <div class="node-detail" v-if="detailNode">
        <el-descriptions v-if="detailNode.type === 'rule'" :column="1" border size="small">
          <el-descriptions-item label="规则编号">
            <el-tag size="mini">{{ detailNode.raw.ruleCode }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="规则名称">{{ detailNode.raw.ruleName }}</el-descriptions-item>
          <el-descriptions-item label="场景">{{ sceneLabel(detailNode.raw.sceneCode) }}</el-descriptions-item>
          <el-descriptions-item label="大类">{{ categoryLabel(detailNode.raw.categoryCode) }}</el-descriptions-item>
          <el-descriptions-item label="费用类型">{{ detailNode.raw.feeType || '通用' }}</el-descriptions-item>
          <el-descriptions-item label="文档类型">{{ detailNode.raw.docType || '-' }}</el-descriptions-item>
          <el-descriptions-item label="文件夹要求">
            <pre class="multi-line">{{ detailNode.raw.folderReq || '-' }}</pre>
          </el-descriptions-item>
          <el-descriptions-item label="文件格式">
            <pre class="multi-line">{{ detailNode.raw.fileFormat || '-' }}</pre>
          </el-descriptions-item>
          <el-descriptions-item label="命名要求">
            <pre class="multi-line">{{ detailNode.raw.namingReq || '-' }}</pre>
          </el-descriptions-item>
          <el-descriptions-item label="启用">
            <el-tag size="mini" :type="detailNode.raw.enabled === 1 ? 'success' : 'info'">
              {{ detailNode.raw.enabled === 1 ? '启用' : '禁用' }}
            </el-tag>
          </el-descriptions-item>
        </el-descriptions>

        <el-descriptions v-else-if="detailNode.type === 'item'" :column="1" border size="small">
          <el-descriptions-item label="检查项编号">
            <el-tag size="mini" type="warning">{{ detailNode.raw.itemCode }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="所属规则">{{ detailNode.raw.ruleCode || '-' }}</el-descriptions-item>
          <el-descriptions-item label="检查项标题">{{ detailNode.raw.itemTitle || '-' }}</el-descriptions-item>
          <el-descriptions-item label="审核内容">
            <pre class="multi-line">{{ detailNode.raw.checkContent || '-' }}</pre>
          </el-descriptions-item>
          <el-descriptions-item label="标准/限额">
            <pre class="multi-line">{{ detailNode.raw.standard || '-' }}</pre>
          </el-descriptions-item>
          <el-descriptions-item label="严重程度">
            <el-tag size="mini" :type="severityType(detailNode.raw.severity)">
              {{ detailNode.raw.severity || 'FAIL' }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="检查类型">{{ detailNode.raw.checkType || '-' }}</el-descriptions-item>
          <el-descriptions-item label="启用">
            <el-tag size="mini" :type="detailNode.raw.enabled === 1 ? 'success' : 'info'">
              {{ detailNode.raw.enabled === 1 ? '启用' : '禁用' }}
            </el-tag>
          </el-descriptions-item>
        </el-descriptions>

        <div v-else style="color:#909399;padding:20px 0">
          这是一个分组节点,包含 {{ detailNode.childCount }} 个子节点。
        </div>
      </div>
    </el-drawer>
  </el-dialog>
</template>

<script>
import { aiRuleApi, aiCheckItemApi } from './api.js';

const SCENE_LABEL = { supplier: '供应商', labor: '劳务', contract: '合同' };
const CATEGORY_LABEL = {
  contract: '合同协议', invoice: '发票', material: '材料',
  settlement: '结算单', meeting: '会议相关', other: '其他', main: '合同主体'
};
const SEVERITY_TYPE = { FAIL: 'danger', WARN: 'warning', INFO: 'info' };

// 颜色方案
const COLOR_SCHEME = {
  root: { bg: '#409EFF', border: '#3375d6', text: '#fff' },
  group: { bg: '#67C23A', border: '#529b22', text: '#fff' },
  rule: { bg: '#E6A23C', border: '#cf7e15', text: '#fff' },
  item: { bg: '#F5F7FA', border: '#bbb', text: '#303133' }
};

export default {
  name: 'AiRuleMindMap',
  data() {
    return {
      dialogVisible: false,
      loading: false,
      rules: [],
      itemsByRuleId: {},  // ruleId -> items[]
      groupBy: 'scene',   // scene | category | feeType
      // 树结构
      nodes: [],
      links: [],
      // 视图变换
      scale: 1,
      panX: 0,
      panY: 0,
      canvasWidth: 4000,
      canvasHeight: 3000,
      // 详情
      detailVisible: false,
      detailNode: null,
      // 拖拽状态
      panning: false,
      panStart: null
    };
  },
  computed: {
    totalRules() { return this.rules.length; },
    totalItems() {
      return Object.values(this.itemsByRuleId).reduce((s, arr) => s + (arr ? arr.length : 0), 0);
    },
    visibleNodes() { return this.nodes.filter(n => n.visible); },
    visibleLinks() { return this.links.filter(l => l.visible); }
  },
  methods: {
    sceneLabel(s) { return SCENE_LABEL[s] || s || '通用'; },
    categoryLabel(c) { return CATEGORY_LABEL[c] || c || '其他'; },
    severityType(s) { return SEVERITY_TYPE[s] || 'info'; },

    /** 外部调用打开 */
    open() {
      this.dialogVisible = true;
      this.loadAll();
    },

    async loadAll() {
      this.loading = true;
      try {
        // 一次拉全(规则数量不会超过 200)
        const resp = await aiRuleApi.page({ pageNum: 1, pageSize: 500 });
        if (resp.code === 200) {
          this.rules = resp.data.records || [];
          this.rebuild();
        } else {
          this.$message.error(resp.msg || '规则加载失败');
        }
      } catch (e) {
        this.$message.error('网络错误: ' + (e.message || e));
      } finally {
        this.loading = false;
      }
    },

    /** 按当前分组方式重建节点树 */
    rebuild() {
      const groups = {};
      this.rules.forEach(r => {
        let key;
        if (this.groupBy === 'scene') key = this.sceneLabel(r.sceneCode);
        else if (this.groupBy === 'category') key = this.categoryLabel(r.categoryCode);
        else key = r.feeType || '通用';
        if (!groups[key]) groups[key] = [];
        groups[key].push(r);
      });

      // 节点ID规则:root / g-{name} / r-{ruleId} / i-{itemId}
      const nodes = [];
      const links = [];

      // 中心节点
      const root = {
        id: 'root', type: 'root', label: 'AI 规则库',
        expanded: true, hasChildren: true, childCount: Object.keys(groups).length,
        visible: true, x: 0, y: 0,
        width: 140, height: 50, fontSize: 16,
        bgColor: COLOR_SCHEME.root.bg, borderColor: COLOR_SCHEME.root.border, textColor: COLOR_SCHEME.root.text,
        parentId: null, depth: 0
      };
      nodes.push(root);

      // 分组节点 + 规则节点 + 检查项节点
      const groupNames = Object.keys(groups);
      groupNames.forEach((gName, gi) => {
        const groupRules = groups[gName];
        const gNode = {
          id: `g-${gi}`, type: 'group', label: `${gName} (${groupRules.length})`,
          expanded: true, hasChildren: true, childCount: groupRules.length,
          visible: true,
          width: 130, height: 38, fontSize: 13,
          bgColor: COLOR_SCHEME.group.bg, borderColor: COLOR_SCHEME.group.border, textColor: COLOR_SCHEME.group.text,
          parentId: 'root', depth: 1, raw: { groupName: gName }
        };
        nodes.push(gNode);
        links.push({
          id: `l-root-g${gi}`, from: 'root', to: gNode.id,
          color: COLOR_SCHEME.group.bg, visible: true
        });

        groupRules.forEach(rule => {
          const items = this.itemsByRuleId[rule.id] || [];
          const rNode = {
            id: `r-${rule.id}`, type: 'rule',
            label: `${rule.ruleCode} ${rule.ruleName || ''}`.trim(),
            expanded: false, hasChildren: items.length > 0,
            childCount: items.length || '?',
            visible: true,
            width: 220, height: 32, fontSize: 12,
            bgColor: COLOR_SCHEME.rule.bg, borderColor: COLOR_SCHEME.rule.border, textColor: COLOR_SCHEME.rule.text,
            parentId: gNode.id, depth: 2, raw: rule
          };
          nodes.push(rNode);
          links.push({
            id: `l-${gNode.id}-${rNode.id}`, from: gNode.id, to: rNode.id,
            color: COLOR_SCHEME.rule.bg, visible: true
          });

          items.forEach(it => {
            const iNode = {
              id: `i-${it.id}`, type: 'item',
              label: `${it.itemCode}`,
              expanded: false, hasChildren: false, childCount: 0,
              visible: false,  // 默认折叠 rule 下的 item
              width: 100, height: 26, fontSize: 11,
              bgColor: COLOR_SCHEME.item.bg, borderColor: COLOR_SCHEME.item.border, textColor: COLOR_SCHEME.item.text,
              parentId: rNode.id, depth: 3, raw: it
            };
            nodes.push(iNode);
            links.push({
              id: `l-${rNode.id}-${iNode.id}`, from: rNode.id, to: iNode.id,
              color: '#bbb', visible: false
            });
          });
        });
      });

      this.nodes = nodes;
      this.links = links;
      this.layout();
    },

    /** 横向树状布局 - 计算每个节点的 x/y */
    layout() {
      // 构建 childMap: parentId -> [childIds]
      const childMap = {};
      this.nodes.forEach(n => {
        if (n.parentId) {
          if (!childMap[n.parentId]) childMap[n.parentId] = [];
          childMap[n.parentId].push(n.id);
        }
      });

      const nodeMap = {};
      this.nodes.forEach(n => nodeMap[n.id] = n);

      // 每个深度的 x(列间距)
      const COL_GAP = 240;
      // 每个叶子节点占的 y 高度
      const ROW_GAP = 36;

      // 计算可见子树的叶子数(决定该节点占多少行)
      const leafCount = (id) => {
        const node = nodeMap[id];
        if (!node) return 1;
        const children = (childMap[id] || []).filter(cid => nodeMap[cid] && nodeMap[cid].visible);
        if (children.length === 0) return 1;
        return children.reduce((sum, cid) => sum + leafCount(cid), 0);
      };

      // 递归布局
      const place = (id, x, yStart) => {
        const node = nodeMap[id];
        if (!node) return;
        const children = (childMap[id] || []).filter(cid => nodeMap[cid] && nodeMap[cid].visible);
        const myLeafCount = leafCount(id);
        node.x = x;
        node.y = yStart + (myLeafCount * ROW_GAP) / 2;

        let yCursor = yStart;
        children.forEach(cid => {
          const cLeaf = leafCount(cid);
          place(cid, x + COL_GAP, yCursor);
          yCursor += cLeaf * ROW_GAP;
        });
      };

      place('root', 200, 100);

      // 计算 canvas 大小:右边和下边都留余量
      let maxX = 0, maxY = 0;
      this.nodes.filter(n => n.visible).forEach(n => {
        maxX = Math.max(maxX, n.x + n.width);
        maxY = Math.max(maxY, n.y + n.height);
      });
      this.canvasWidth = Math.max(1200, maxX + 200);
      this.canvasHeight = Math.max(800, maxY + 100);

      // 计算每条连线的 path(贝塞尔曲线)
      this.links.forEach(link => {
        const from = nodeMap[link.from];
        const to = nodeMap[link.to];
        if (!from || !to || !from.visible || !to.visible) {
          link.visible = false;
          return;
        }
        link.visible = true;
        const x1 = from.x + from.width / 2;
        const y1 = from.y;
        const x2 = to.x - to.width / 2;
        const y2 = to.y;
        const dx = (x2 - x1) / 2;
        link.path = `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`;
      });
    },

    /** 节点点击:展开/收起 */
    async onNodeClick(node) {
      if (node.type === 'root') return;
      if (!node.hasChildren) return;

      // rule 节点首次展开:加载检查项
      if (node.type === 'rule' && !this.itemsByRuleId[node.raw.id]) {
        try {
          const resp = await aiCheckItemApi.listByRule(node.raw.id);
          if (resp.code === 200) {
            this.itemsByRuleId[node.raw.id] = resp.data || [];
            // 重建以加入 item 节点
            this.rebuild();
            // 重新拿到节点引用(rebuild 后对象不同了)
            const newNode = this.nodes.find(n => n.id === node.id);
            if (newNode) {
              newNode.expanded = true;
              newNode.hasChildren = (resp.data || []).length > 0;
              this.toggleChildren(newNode, true);
            }
            this.layout();
            return;
          }
        } catch (e) {
          this.$message.error('加载检查项失败');
          return;
        }
      }

      // 切换展开
      node.expanded = !node.expanded;
      this.toggleChildren(node, node.expanded);
      this.layout();
    },

    /** 递归切换子节点可见性 */
    toggleChildren(node, show) {
      const children = this.nodes.filter(n => n.parentId === node.id);
      children.forEach(c => {
        c.visible = show;
        // 如果是收起,把子树全部收起
        if (!show) {
          c.expanded = false;
          this.toggleChildren(c, false);
        } else if (c.expanded) {
          // 如果原本展开就保持展开
          this.toggleChildren(c, true);
        }
      });
    },

    /** 展开所有 */
    async expandAll() {
      // 先加载所有未加载的检查项
      const missingRuleIds = this.rules
        .filter(r => !this.itemsByRuleId[r.id])
        .map(r => r.id);
      if (missingRuleIds.length > 0) {
        this.loading = true;
        try {
          await Promise.all(missingRuleIds.map(async rid => {
            try {
              const resp = await aiCheckItemApi.listByRule(rid);
              if (resp.code === 200) this.itemsByRuleId[rid] = resp.data || [];
            } catch (e) { /* 忽略单条失败 */ }
          }));
          this.rebuild();
        } finally {
          this.loading = false;
        }
      }
      this.nodes.forEach(n => {
        if (n.hasChildren) n.expanded = true;
        if (n.parentId) n.visible = true;
      });
      this.layout();
    },

    /** 全部收起到根 */
    collapseAll() {
      this.nodes.forEach(n => {
        n.expanded = false;
        if (n.depth >= 2) n.visible = false;
      });
      this.nodes.find(n => n.id === 'root').expanded = true;
      this.nodes.filter(n => n.depth === 1).forEach(n => n.visible = true);
      this.layout();
    },

    showDetail(node) {
      this.detailNode = node;
      this.detailVisible = true;
    },

    /** 缩放 */
    zoom(factor) {
      this.scale = Math.max(0.3, Math.min(3, this.scale * factor));
    },

    onWheel(e) {
      if (e.ctrlKey || e.metaKey) {
        this.zoom(e.deltaY < 0 ? 1.1 : 0.9);
      } else {
        this.panY -= e.deltaY;
        this.panX -= e.deltaX;
      }
    },

    /** 拖动平移 */
    onPanStart(e) {
      if (e.target.tagName === 'circle' || e.target.tagName === 'rect' ||
          e.target.tagName === 'text' || e.target.closest('.node')) {
        return;  // 不在节点上才允许平移
      }
      this.panning = true;
      this.panStart = { x: e.clientX - this.panX, y: e.clientY - this.panY };
    },
    onPanMove(e) {
      if (!this.panning) return;
      this.panX = e.clientX - this.panStart.x;
      this.panY = e.clientY - this.panStart.y;
    },
    onPanEnd() {
      this.panning = false;
    },

    /** 还原视图 */
    resetView() {
      this.scale = 1;
      this.panX = 0;
      this.panY = 0;
    },

    onOpened() {
      // 弹窗动画结束后再 layout 一次,确保尺寸计算正确
      this.$nextTick(() => this.layout());
    },

    onClose() {
      // 收起所有,下次打开是干净的
      this.collapseAll();
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep .mindmap-dialog .el-dialog__body {
  padding: 0;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 54px);
  overflow: hidden;
}

.mindmap-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  background: #f5f7fa;
  border-bottom: 1px solid #e6e9f0;
  flex-shrink: 0;

  .title-hint {
    font-size: 13px;
    color: #606266;

    i { color: #409EFF; margin-right: 4px; }
  }

  .toolbar-right {
    display: flex;
    align-items: center;
    gap: 6px;
  }
}

.mindmap-canvas-wrap {
  flex: 1;
  overflow: hidden;
  background:
    radial-gradient(circle, #e0e6ee 1px, transparent 1px) 0 0 / 20px 20px,
    linear-gradient(135deg, #fafbfd, #f0f3f8);
  cursor: grab;
  position: relative;

  &:active {
    cursor: grabbing;
  }

  > div {
    width: 100%;
    height: 100%;
    overflow: hidden;
  }
}

.mindmap-svg {
  transform-origin: 0 0;
  transition: transform 0.05s linear;

  .node {
    cursor: pointer;

    rect {
      transition: filter 0.2s;
    }

    &:hover rect {
      filter: drop-shadow(0 2px 6px rgba(0,0,0,0.2));
    }
  }
}

.node-detail {
  padding: 0 20px;

  .multi-line {
    margin: 0;
    white-space: pre-wrap;
    word-break: break-word;
    font-family: inherit;
    font-size: 12px;
    line-height: 1.6;
  }
}
</style>
